import { useMemo, useState } from 'react';
import {
  billedPct,
  formatHours,
  formatMoney,
  occurrenceState,
  type Contract,
  type ContractWorklog,
  type Invoice,
  type LineItem,
  type Occurrence,
  type OccurrenceEconomics,
} from '@forecastify/shared';
import {
  Badge,
  Button,
  DataList,
  Icon,
  Progress,
  Tooltip,
  type DataListRow,
  type MenuItem,
} from '@forecastify/ui';
import { useT } from '../../i18n';
import {
  CodeChip,
  DetailFrame,
  MoreMenu,
  RailCard,
  RailLink,
  RailRow,
  RailValue,
  SectionAction,
  SectionHeader,
} from './detail-chrome';
import { statusLabel as invoiceStatusLabel } from './invoice-list';
import { contractLabel } from './contract-label';
import { listLabels } from '../delivery/clients-screen';
import {
  CONTRACT_LEVEL,
  PERIOD_GROUPS,
  type PeriodGroup,
  type PeriodLogs,
} from './use-period-logs';
import { personCell } from '../person-cell';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C29 · One period of a recurring contract. `2fazis.PeriodDetail.html`.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A SCREEN ABOUT SOMETHING THAT IS NOT A ROW.** A period is derived (P2-3) — there is no
 * table, no id and no `version`. Everything here is computed from the recurrence, the calendar and
 * the contract's worklogs, and the only thing stored is the one human override: a closed period
 * somebody reopened for late entry (`0056`).
 *
 * ⚠️ **What the mockup draws that P2-1 defers**: *Külső költségek* and *Elszámolható — kereten
 * felül*. Its own logic never fills two more of them either (`showPlan` and `showOt` are hardcoded
 * `false`), so *Terv* and *Túlóra* are absent for a second reason: overtime is not a Phase 2
 * concept.
 *
 * ⚠️⚠️ **C57 ADDED THE MONEY CARD AND *Számlák*, AND THE PAIRING IS THE POINT.** *Kiszámlázva* on
 * its own is a figure with nothing to be a share of — the question somebody asks on this screen is
 * *how much of this month have we billed*, which needs the period's revenue beside it. So the card
 * arrives with both halves rather than the invoice half alone.
 */

export interface PeriodDetailData {
  readonly contract: Contract;
  readonly lineItems: readonly LineItem[];
  readonly period: Occurrence;
  /** How many periods the contract has, for `#3 / 12` and the arrows. */
  readonly total: number;
  /**
   * 31 Oct 2026 · the period's logs, paged on the server, with the window's figures beside them —
   * `use-period-logs.ts`. It held every worklog of the contract, up to a silent 5,000.
   */
  readonly logs: PeriodLogs;
  readonly today: string;
  /** The collector task, when the contract has one — P2-10. */
  readonly collector: { readonly id: string; readonly key: string } | null;
  /**
   * C55/C57 · this period's own money. ⚠️ **Read separately and NON-FATAL** — the figures are a
   * pass over every line item, worklog and allocation of the contract, so `null` is *not loaded*
   * and the card simply does not appear, which is the rule the line-item detail already follows.
   */
  readonly economics: OccurrenceEconomics | null;
  /** C57 · the invoices that count against this period. Empty is the usual answer. */
  readonly invoices: readonly Invoice[];
  readonly currencies: readonly { readonly code: string; readonly symbol: string }[];
}

export interface PeriodDetailHandlers {
  /** `contract.manage` — reopening a month is not logging time. See `contract.service.ts`. */
  readonly canManage: boolean;
  readonly onOpenContract: () => void;
  readonly onOpenProject: () => void;
  readonly onOpenTask: (taskId: string) => void;
  readonly onStep: (period: Occurrence) => void;
  /** Reopens a closed period for late entry, or closes it again. */
  readonly onSetOpen: (open: boolean) => void;
  /** C57 · raises an invoice against this period, with what is left to bill filled in. */
  readonly onBill?: () => void;
  readonly onOpenInvoice?: (invoice: Invoice) => void;
  readonly onExport: () => void;
  readonly onExpand?: () => void;
}

export function PeriodDetail({
  data,
  handlers,
  wide,
}: {
  readonly data: PeriodDetailData;
  readonly handlers: PeriodDetailHandlers;
  readonly wide: boolean;
}) {
  return (
    <DetailFrame
      wide={wide}
      header={<Header data={data} handlers={handlers} />}
      rail={
        <>
          <StatusCard data={data} handlers={handlers} />
          <MoneyCard data={data} handlers={handlers} />
          <DetailsCard data={data} handlers={handlers} />
        </>
      }
      main={
        <>
          <Logs data={data} handlers={handlers} wide={wide} />
          {/* 22 Oct 2026 · an invoice is money through and through (`project-reach.ts`). */}
          {data.contract.moneyVisible ? <InvoicesCard data={data} handlers={handlers} /> : null}
        </>
      }
    />
  );
}

/**
 * ⚠️ **Reopening is applied OVER the calendar, never instead of it.** `occurrenceState` answers from
 * the dates; a reopened closed period reads as active, and taking the override away closes it again
 * because closed is what the dates say. `0056` argues the storage; this is the reading.
 */
function stateOf(data: PeriodDetailData): {
  state: 'planned' | 'active' | 'closed';
  reopened: boolean;
} {
  const state = occurrenceState(data.period, data.today);
  const reopened = state === 'closed' && data.contract.reopenedPeriods.includes(data.period.from);
  return { state: reopened ? 'active' : state, reopened };
}

const STATE_KEY = {
  planned: 'occurrence.state.planned',
  active: 'occurrence.state.active',
  closed: 'occurrence.state.closed',
} as const;

const STATE_COLOR = {
  planned: 'var(--muted-foreground)',
  active: 'var(--primary)',
  closed: 'var(--muted-foreground)',
} as const;

function Header({
  data,
  handlers,
}: {
  readonly data: PeriodDetailData;
  readonly handlers: PeriodDetailHandlers;
}) {
  const t = useT();
  const { state, reopened } = stateOf(data);

  const menu: MenuItem[] = [
    ...(handlers.onExpand === undefined
      ? []
      : [{ label: t('task.mode.full'), icon: 'maximize' as const, onSelect: handlers.onExpand }]),
    { label: t('lineItem.openContract'), icon: 'file-text', onSelect: handlers.onOpenContract },
    { label: t('period.export'), icon: 'download', onSelect: handlers.onExport },
  ];

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <CodeChip
          code={`${contractLabel(data.contract)}/#${String(data.period.index)}`}
          icon="arrow-right"
          title={t('lineItem.openContract')}
          onClick={handlers.onOpenContract}
        />
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-[12px] transition-colors"
          onClick={handlers.onOpenProject}
        >
          <span
            className="size-2 rounded-[3px]"
            style={{ background: data.contract.projectColor ?? 'transparent' }}
            aria-hidden
          />
          {data.contract.projectName}
        </button>
        {data.period.partial ? <Badge variant="outline">{t('occurrence.partial')}</Badge> : null}

        <div className="flex-1" />

        {/* ⚠️ **The stepper is the mockup's own**, and it is what makes this screen worth opening:
            a month at a time, in place, rather than back to the contract and down again. */}
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon-sm"
            disabled={data.period.index <= 1}
            aria-label={t('period.previous')}
            title={t('period.previous')}
            onClick={() => {
              handlers.onStep({ ...data.period, index: data.period.index - 1 });
            }}
          >
            <Icon name="chevron-left" size={14} />
          </Button>
          <span className="text-muted-foreground w-[52px] text-center text-[11.5px] tabular-nums">
            #{data.period.index} / {data.total}
          </span>
          <Button
            variant="outline"
            size="icon-sm"
            disabled={data.period.index >= data.total}
            aria-label={t('period.next')}
            title={t('period.next')}
            onClick={() => {
              handlers.onStep({ ...data.period, index: data.period.index + 1 });
            }}
          >
            <Icon name="chevron-right" size={14} />
          </Button>
        </div>

        <MoreMenu items={menu} label={t('common.more')} />
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <span className="text-[19px] leading-snug font-semibold">
          #{data.period.index} · {data.period.from} – {data.period.to}
        </span>
        <span className="bg-muted flex h-[22px] items-center gap-1.5 rounded-full px-2.5 text-[11.5px] font-medium">
          <span
            className="size-2 rounded-full"
            style={{ background: STATE_COLOR[state] }}
            aria-hidden
          />
          {t(STATE_KEY[state])}
          {reopened ? (
            <span className="text-muted-foreground">· {t('period.reopened')}</span>
          ) : null}
        </span>
      </div>
    </>
  );
}

function StatusCard({
  data,
  handlers,
}: {
  readonly data: PeriodDetailData;
  readonly handlers: PeriodDetailHandlers;
}) {
  const t = useT();
  const { state, reopened } = stateOf(data);

  /* 31 Oct 2026 · the window's hours, counted by the server — the page holds only 25 rows. */
  const fact = Number(data.logs.summary?.windowHours ?? 0);
  /**
   * ⚠️ **The budget is the line items' per-period estimate, summed — no division.** `0050` stores
   * the per-period figure, so the sum already IS one period's commitment. The mockup divides
   * because it stores the total; P2-11 records which way round we went.
   */
  const budget = data.lineItems.reduce((sum, item) => sum + Number(item.estimateHours), 0);
  const diff = fact - budget;
  const pct = budget > 0 ? Math.min(100, Math.round((fact / budget) * 100)) : 0;

  /**
   * ⚠️⚠️ **ANY ENDED PERIOD CAN BE REOPENED, WHATEVER ITS HOURS** (30 Oct 2026, the product owner).
   * It was offered only on a closed period that was SHORT — the mockup's `canReopen` — while the
   * period list offered it disabled and the server and the agent allowed it on any closed one:
   * three answers to one question. A running period is not closed, so there is nothing to reopen.
   */
  const canReopen = state === 'closed';

  return (
    <RailCard title={t('contract.column.status')} padded>
      <div className="mt-1.5 flex items-center gap-2">
        <span
          className="size-2.5 shrink-0 rounded-full"
          style={{ background: STATE_COLOR[state] }}
          aria-hidden
        />
        <span className="flex-1 text-[13px] font-medium">{t(STATE_KEY[state])}</span>
        {/* ⚠️ A closed period says why it is closed: nobody closed it, its time ran out. */}
        {state === 'closed' ? (
          <Tooltip content={t('period.closedWhy')} width={260}>
            <Icon name="circle-help" size={13} className="text-muted-foreground" aria-hidden />
          </Tooltip>
        ) : (
          <Icon name="clock" size={13} className="text-muted-foreground" />
        )}
      </div>

      <div className="mt-3 flex items-center justify-between text-[11.5px]">
        <span className="text-muted-foreground">
          {t('period.factOfBudget', {
            fact: formatHours(String(fact)),
            budget: budget > 0 ? formatHours(String(budget)) : '–',
          })}
        </span>
        <span className={`tabular-nums ${diff > 0.004 ? 'text-destructive' : ''}`}>
          {budget > 0 ? `${String(Math.round((fact / budget) * 100))}%` : '–'}
        </span>
      </div>
      <Progress
        value={pct}
        label={t('occurrence.effort')}
        className="mt-1.5"
        {...(diff > 0.004 ? { color: 'var(--destructive)' } : {})}
      />

      <div className="mt-3 flex flex-col gap-1 border-t pt-2.5">
        <Figure label={t('period.budget')} value={budget > 0 ? formatHours(String(budget)) : '–'} />
        <Figure label={t('period.fact')} value={formatHours(String(fact))} />
        <Figure
          label={t('period.difference')}
          value={`${diff > 0.004 ? '+' : diff < -0.004 ? '−' : ''}${formatHours(String(Math.abs(diff)))}`}
          className={diff > 0.004 ? 'text-destructive' : 'text-muted-foreground'}
        />
      </div>

      {handlers.canManage && (canReopen || reopened) ? (
        <div className="mt-3 border-t pt-3">
          <Button
            variant={reopened ? 'outline' : 'default'}
            size="sm"
            className="w-full gap-1.5"
            onClick={() => {
              handlers.onSetOpen(!reopened);
            }}
          >
            <Icon name={reopened ? 'circle-check' : 'refresh-cw'} size={13} />
            {t(reopened ? 'period.close' : 'period.reopen')}
          </Button>
          <div className="text-muted-foreground mt-1.5 text-[11px] leading-relaxed">
            {t(
              reopened
                ? diff < -0.004
                  ? 'period.stillShort'
                  : 'period.metItsBudget'
                : 'period.reopenHint',
              { hours: formatHours(String(Math.abs(diff))) },
            )}
          </div>
        </div>
      ) : null}
    </RailCard>
  );
}

function Figure({
  label,
  value,
  className,
}: {
  readonly label: string;
  readonly value: string;
  readonly className?: string;
}) {
  return (
    <div className="flex items-center justify-between text-[11.5px]">
      <span className="text-muted-foreground">{label}</span>
      <span className={`tabular-nums ${className ?? ''}`}>{value}</span>
    </div>
  );
}

function DetailsCard({
  data,
  handlers,
}: {
  readonly data: PeriodDetailData;
  readonly handlers: PeriodDetailHandlers;
}) {
  const t = useT();
  const people = data.logs.summary?.people.length ?? 0;

  return (
    <RailCard title={t('contract.details')}>
      <RailRow label={t('contract.column.project')}>
        <RailLink
          /* ⚠️ C56 · a period only exists on a recurring contract, which may still be
             client-scoped — an em-dash rather than a blank row. */
          value={data.contract.projectName ?? '–'}
          title={t('contract.action.openProject')}
          onClick={handlers.onOpenProject}
          before={
            <span
              className="size-2 shrink-0 rounded-[3px]"
              style={{ background: data.contract.projectColor ?? 'transparent' }}
              aria-hidden
            />
          }
        />
      </RailRow>
      <RailRow label={t('title.contracts.one')}>
        <RailLink
          value={data.contract.name}
          title={t('lineItem.openContract')}
          onClick={handlers.onOpenContract}
        />
      </RailRow>

      <RailRow label={t('period.index')} divided>
        <RailValue>
          #{data.period.index} / {data.total}
        </RailValue>
      </RailRow>
      <RailRow label={t('contract.column.startsOn')}>
        <RailValue>{data.period.from}</RailValue>
      </RailRow>
      <RailRow label={t('contract.column.endsOn')}>
        <RailValue>{data.period.to}</RailValue>
      </RailRow>
      {data.period.partial ? (
        <p className="text-muted-foreground px-2 pt-0.5 text-[11px] leading-relaxed">
          {t('period.partialNote', { natural: data.period.naturalTo, actual: data.period.to })}
        </p>
      ) : null}

      <RailRow label={t('contract.tasks')} divided>
        {/* ⚠️ The collector, which is where a recurring contract's hours land (P2-10). */}
        {data.collector === null ? (
          <RailValue mono={false}>{t('period.noCollector')}</RailValue>
        ) : (
          <RailLink
            value={data.collector.key}
            title={t('lineItem.action.open')}
            onClick={() => {
              if (data.collector !== null) handlers.onOpenTask(data.collector.id);
            }}
          />
        )}
      </RailRow>
      <RailRow label={t('contract.lineItems')}>
        <RailValue>{data.lineItems.length}</RailValue>
      </RailRow>
      <RailRow label={t('period.people')}>
        <RailValue>{people}</RailValue>
      </RailRow>
    </RailCard>
  );
}

/**
 * The period's worklogs, as `DataList` — search, filters, multi-column sort and the settled
 * `[<] [selector] [>] [total]` pager.
 *
 * ⚠️⚠️ **THE MOCKUP'S GROUPING IS BACK — 17 Oct 2026, the product owner — OVER THE WHOLE FILTERED
 * SET.** ux-pass-2 removed it because it grouped WITHIN a page: a header saying twelve hours could
 * show three, and the next page reopened the group. Now a group is computed from every row the
 * filters left, its header carries that whole total, and a grouped list is NOT paged — the groups
 * fold instead. *Nincs csoport* keeps the pager.
 *
 * ⚠️⚠️ **31 Oct 2026 · PAGED, FILTERED AND SORTED ON THE SERVER** — `use-period-logs.ts`. The
 * options are still the people, tasks and line items THIS period has hours on, now from the
 * summary rather than from the rows held; a grouped view reads its whole filtered set.
 */
function Logs({
  data,
  handlers,
  wide,
}: {
  readonly data: PeriodDetailData;
  readonly handlers: PeriodDetailHandlers;
  readonly wide: boolean;
}) {
  const t = useT();
  const logs = data.logs;
  const summary = logs.summary;
  const group = logs.group;
  /* Folded groups, by key — every group starts open. */
  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set());

  const people = useMemo(
    () => (summary?.people ?? []).map((one) => ({ value: one.id, label: one.name })),
    [summary],
  );
  const tasks = useMemo(
    () =>
      (summary?.tasks ?? []).map((one) => ({ value: one.id, label: `${one.key} · ${one.title}` })),
    [summary],
  );
  const items = useMemo(
    () =>
      (summary?.lineItemIds ?? [])
        .map((id) => ({
          value: id ?? CONTRACT_LEVEL,
          label:
            id === null
              ? t('period.contractLevel')
              : (data.lineItems.find((one) => one.id === id)?.name ?? '–'),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [summary, data.lineItems, t],
  );

  const rowOf = (log: ContractWorklog): DataListRow => ({
    id: log.id,
    cells: {
      date: { text: log.workedOn, mono: true, muted: true },
      who: personCell(log.memberName, { seed: log.memberAvatarSeed }),
      key: { text: log.taskKey, mono: true, muted: true },
      task: { text: log.taskTitle },
      /* ⚠️ The collector carries no line item of its own, and the icon says why this cell names
         the contract rather than looking unfilled. */
      lineItem:
        log.lineItemId === null
          ? { icon: 'repeat' as const, text: t('period.contractLevel'), muted: true }
          : { text: nameOfItem(log, data) ?? '–', muted: true },
      hours: { text: formatHours(log.hours), mono: true },
    },
  });

  /** A log's group: its key and the title its header shows. */
  const groupOf = (log: ContractWorklog): { key: string; title: string } => {
    if (group === 'member') {
      return { key: log.memberId ?? '-', title: log.memberName ?? '–' };
    }
    if (group === 'lineItem') {
      return {
        key: log.lineItemId ?? CONTRACT_LEVEL,
        title: nameOfItem(log, data) ?? t('period.contractLevel'),
      };
    }
    if (group === 'day') return { key: log.workedOn, title: log.workedOn };
    return { key: log.taskId, title: `${log.taskKey} · ${log.taskTitle}` };
  };

  /**
   * The grouped rows: a header per group — title, entry count and the group's WHOLE total over
   * every filtered row — then its logs unless folded. Groups keep the order their first row has
   * in the server's sort, so a sort still decides what comes first.
   */
  const grouped = useMemo((): DataListRow[] => {
    if (group === 'none') return [];
    const order: string[] = [];
    const byKey = new Map<string, { title: string; logs: ContractWorklog[] }>();
    for (const log of logs.rows) {
      const { key, title } = groupOf(log);
      const entry = byKey.get(key);
      if (entry === undefined) {
        order.push(key);
        byKey.set(key, { title, logs: [log] });
      } else entry.logs.push(log);
    }
    return order.flatMap((key) => {
      const entry = byKey.get(key);
      if (entry === undefined) return [];
      const open = !folded.has(key);
      const hours = entry.logs.reduce((sum, log) => sum + Number(log.hours), 0);
      const header: DataListRow = {
        id: `${GROUP_ROW}${key}`,
        cells: {
          date: {
            icon: open ? ('chevron-down' as const) : ('chevron-right' as const),
            text: entry.title,
            bold: true,
          },
          who: { text: t('period.groupCount', { count: entry.logs.length }), muted: true },
          hours: {
            text: formatHours(String(hours)),
            mono: true,
            bold: true,
          },
        },
      };
      return open ? [header, ...entry.logs.map(rowOf)] : [header];
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `groupOf` and `rowOf` read only
       `group`, `data` and `t`, which are listed. */
  }, [group, logs.rows, folded, data, t]);

  const filtered = logs.query !== '' || logs.person !== '' || logs.task !== '' || logs.item !== '';

  return (
    <section>
      <SectionHeader
        title={t('period.logs')}
        note={t('period.logCount', { count: summary?.windowCount ?? 0 })}
      />

      <div className="mt-1.5 overflow-hidden rounded-lg border">
        <DataList
          columnsKey="period-logs"
          card={{
            title: 'task',
            context: 'date',
            defaults: { avatar: 'who', details: ['key', 'lineItem'] },
          }}
          selectable={false}
          columns={[
            { key: 'date', label: t('time.column.date'), width: 96, sortKey: 'date' },
            { key: 'who', label: t('period.who'), width: 150, sortKey: 'who' },
            { key: 'key', label: t('lineItem.taskKey'), width: 96, sortKey: 'key' },
            { key: 'task', label: t('contract.tasks'), sortKey: 'task' },
            /* ⚠️ The drawer has no room for a fifth text column — the narrow frame drops it, as
               the hand-built table did; the line item filter still works there. */
            ...(wide
              ? [
                  {
                    key: 'lineItem',
                    label: t('contract.lineItem'),
                    width: 160,
                    sortKey: 'lineItem',
                  },
                ]
              : []),
            {
              key: 'hours',
              label: t('common.hours'),
              width: 72,
              align: 'right' as const,
              sortKey: 'hours',
            },
          ]}
          rows={group === 'none' ? logs.rows.map(rowOf) : grouped}
          {...(group === 'none' ? { page: logs.page } : {})}
          isRefreshing={logs.loading}
          viewSwitch={{
            value: group,
            items: PERIOD_GROUPS.map((value) => ({ value, label: groupLabel(t, value) })),
            onChange: (value) => {
              const next = PERIOD_GROUPS.find((one) => one === value);
              if (next !== undefined) {
                logs.setGroup(next);
                setFolded(new Set());
              }
            },
          }}
          sort={logs.sort}
          onSortChange={logs.setSort}
          search={logs.query}
          onSearchChange={logs.setQuery}
          filters={[
            {
              key: 'who',
              label: t('period.who'),
              value: logs.person,
              onChange: logs.setPerson,
              options: people,
            },
            {
              key: 'task',
              label: t('contract.tasks'),
              value: logs.task,
              onChange: logs.setTask,
              options: tasks,
            },
            {
              key: 'lineItem',
              label: t('contract.lineItem'),
              value: logs.item,
              onChange: logs.setItem,
              options: items,
            },
          ]}
          onRowClick={(id) => {
            /* A group header folds and unfolds its group. */
            if (id.startsWith(GROUP_ROW)) {
              const key = id.slice(GROUP_ROW.length);
              setFolded((current) => {
                const next = new Set(current);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
              });
              return;
            }
            const log = logs.rows.find((one) => one.id === id);
            if (log !== undefined) handlers.onOpenTask(log.taskId);
          }}
          empty={{
            icon: 'clock',
            title: t(filtered ? 'period.emptyFiltered' : 'period.empty'),
            body: '',
          }}
          /* ⚠️ The FILTERED set's count and hours, from the server — never a sum of one page. */
          footerNote={t('period.total', {
            count: summary?.count ?? 0,
            hours: formatHours(summary?.hours ?? '0'),
          })}
          labels={{ ...listLabels(t), search: t('period.search') }}
        />
      </div>
    </section>
  );
}

/** A group header row's id prefix — never a worklog id, which is a uuid. */
const GROUP_ROW = 'group:';

function groupLabel(t: ReturnType<typeof useT>, group: PeriodGroup): string {
  if (group === 'member') return t('period.group.member');
  if (group === 'lineItem') return t('period.group.lineItem');
  if (group === 'day') return t('period.group.day');
  if (group === 'task') return t('period.group.task');
  return t('period.group.none');
}

function nameOfItem(log: ContractWorklog, data: PeriodDetailData): string | null {
  if (log.lineItemId === null) return null;
  return data.lineItems.find((item) => item.id === log.lineItemId)?.name ?? null;
}

/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ PÉNZÜGY — C55's per-period figures, with C57's billing under them.  │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **`Kereten felül` is its own row rather than folded into the revenue.** A `billable` line item
 * neither carries nor consumes the frame — *"nem visz és nem fogyaszt keretet"* — so adding it in
 * would make a period look like it had earned its retainer when it had not. Absent where there is
 * none, because a permanent `0 Ft` row teaches a reader to stop looking at it.
 */
function MoneyCard({
  data,
  handlers,
}: {
  readonly data: PeriodDetailData;
  readonly handlers: PeriodDetailHandlers;
}) {
  const t = useT();
  const money = data.economics;
  if (money === null) return null;

  const code = money.currencyCode;
  const symbol = data.currencies.find((one) => one.code === code)?.symbol ?? code ?? '';
  const show = (value: string): string =>
    code === null ? '–' : formatMoney(Number(value), { code, symbol });

  const live = data.invoices.filter((one) => one.status !== 'void');
  const latest = [...live].sort((a, b) => b.issuedOn.localeCompare(a.issuedOn))[0];
  const billed = billedPct(Number(money.revenue), Number(money.invoiced));

  return (
    <RailCard title={t('occurrence.money')}>
      <RailRow label={t('lineItem.money.budget')}>
        <RailValue>{show(money.budget)}</RailValue>
      </RailRow>
      <RailRow label={t('lineItem.money.revenue')}>
        <RailValue>{show(money.revenue)}</RailValue>
      </RailRow>
      {Number(money.overFrameRevenue) <= 0.004 ? null : (
        <RailRow label={t('occurrence.money.overFrame')}>
          <RailValue>{show(money.overFrameRevenue)}</RailValue>
        </RailRow>
      )}
      {/* 17 Oct 2026 · *Költés* — the period's logged hours at cost. */}
      <RailRow label={t('money.spend')}>
        <RailValue>{show(money.cost)}</RailValue>
      </RailRow>

      <RailRow label={t('lineItem.money.invoiced')} divided>
        <RailValue>{show(money.invoiced)}</RailValue>
      </RailRow>
      <RailRow label={t('lineItem.money.remaining')}>
        <RailValue>{show(money.remaining)}</RailValue>
      </RailRow>

      {billed === null ? null : (
        <div className="px-1 pt-1">
          <div className="bg-muted h-1.5 overflow-hidden rounded-full">
            <div
              className="h-full"
              style={{
                width: `${String(Math.min(100, billed))}%`,
                /* ⚠️ Capped, with the COLOUR saying it went over — a bar drawn past its own track
                   is clipped and says nothing. */
                background: billed > 100 ? 'var(--destructive)' : 'var(--primary)',
              }}
            />
          </div>
          <p className="text-muted-foreground mt-1 text-[11px]">
            {t('invoice.billedPct', { pct: billed })}
          </p>
        </div>
      )}

      <div className="mt-1.5 flex items-center gap-2 px-1">
        {latest === undefined ? (
          <span className="text-muted-foreground text-[11.5px]">{t('invoice.none')}</span>
        ) : (
          <button
            type="button"
            className="text-primary flex min-w-0 items-center gap-1 text-[11.5px] hover:underline"
            onClick={() => handlers.onOpenInvoice?.(latest)}
          >
            <Icon name="file-text" size={12} />
            <span className="truncate tabular-nums">{latest.externalId}</span>
            {live.length > 1 ? (
              <span className="text-muted-foreground">{`+${String(live.length - 1)}`}</span>
            ) : null}
          </button>
        )}
        <div className="flex-1" />
        {handlers.onBill === undefined ? null : (
          <Button
            variant="outline"
            size="sm"
            className="h-[26px] gap-1.5 px-2 text-[11px]"
            onClick={handlers.onBill}
          >
            <Icon name="file-text" size={12} />
            {latest === undefined ? t('invoice.bill') : t('invoice.billMore')}
          </Button>
        )}
      </div>
    </RailCard>
  );
}

/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ SZÁMLÁK — C57, on the period they bill.                           │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A LINE-ITEM INVOICE DATED INSIDE THIS PERIOD IS LISTED HERE TOO, and that is the mockup's
 * own `occInvoices` rather than a convenience.** A fixed deliverable billed mid-frame belongs to the
 * month it was billed in; showing only the invoices *named* at the occurrence would make the same
 * money disappear from every period of the contract.
 */
function InvoicesCard({
  data,
  handlers,
}: {
  readonly data: PeriodDetailData;
  readonly handlers: PeriodDetailHandlers;
}) {
  const t = useT();
  const invoices = data.invoices;

  const code = data.economics?.currencyCode ?? invoices[0]?.currencyCode ?? null;
  const symbol = data.currencies.find((one) => one.code === code)?.symbol ?? code ?? '';
  const show = (value: string, own: string): string =>
    formatMoney(Number(value), {
      code: own,
      symbol: data.currencies.find((one) => one.code === own)?.symbol ?? own,
    });

  const total = invoices
    .filter((one) => one.status !== 'void')
    .reduce((sum, one) => sum + Number(one.amount), 0);

  return (
    <section className="mt-5">
      <SectionHeader
        title={t('invoice.section')}
        note={
          invoices.length === 0 || code === null
            ? undefined
            : t('invoice.section.note', {
                count: invoices.length,
                amount: formatMoney(total, { code, symbol }),
              })
        }
      >
        {handlers.onBill === undefined ? null : (
          <SectionAction
            label={invoices.length === 0 ? t('invoice.bill') : t('invoice.billMore')}
            onClick={handlers.onBill}
          />
        )}
      </SectionHeader>

      {invoices.length === 0 ? (
        <p className="text-muted-foreground bg-card mt-1.5 rounded-xl border p-4 text-center text-[12.5px] shadow-sm">
          {t('invoice.section.emptyPeriod')}
        </p>
      ) : (
        <div className="bg-card mt-1.5 rounded-xl border shadow-sm">
          {[...invoices]
            .sort((a, b) => b.issuedOn.localeCompare(a.issuedOn))
            .map((invoice) => (
              <button
                key={invoice.id}
                type="button"
                className="hover:bg-muted/50 flex w-full items-center gap-3 border-b px-3 py-2 text-left transition-colors last:border-0"
                onClick={() => handlers.onOpenInvoice?.(invoice)}
              >
                <span className="text-muted-foreground w-[72px] shrink-0 text-[11px] tabular-nums">
                  {invoice.identifier}
                </span>
                <span className="min-w-0 flex-1 truncate text-[12px] tabular-nums">
                  {invoice.externalId}
                </span>
                <span className="text-muted-foreground w-[84px] shrink-0 text-[11.5px] tabular-nums">
                  {invoice.issuedOn}
                </span>
                {/* 30 Oct 2026 · fixed cells, so the columns line up row to row (UAT). */}
                <span className="flex w-[92px] shrink-0 justify-end">
                  <Badge variant={invoice.status === 'paid' ? 'secondary' : 'outline'}>
                    {invoiceStatusLabel(t, invoice.status)}
                  </Badge>
                </span>
                <span className="w-[112px] shrink-0 text-right text-[12px] tabular-nums">
                  {show(invoice.amount, invoice.currencyCode)}
                </span>
              </button>
            ))}
        </div>
      )}
    </section>
  );
}
